import styled from '@emotion/styled';
import {withStateHandlers} from 'recompose';
import {form, formProps} from 'lib';
import {mutate} from 'api';
import {Submit, colors, fonts} from 'ui';

const Overlay = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
});

const Sheet = styled('div')({
  backgroundColor: colors.white,
  borderRadius: '20px 20px 0 0',
  width: '100%',
  maxWidth: 500,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const ModalForm = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  padding: '30px 28px 50px',
});

const CloseButton = styled('button')({
  background: 'none',
  border: 'none',
  fontSize: 22,
  cursor: 'pointer',
  color: colors.black,
  padding: 0,
  marginBottom: 20,
});

const Title = styled('h2')({
  fontSize: 26,
  fontFamily: fonts.title,
  fontWeight: 'bold',
  color: colors.black,
  textAlign: 'center',
  margin: '0 0 16px',
});

const Description = styled('p')({
  fontSize: 14,
  lineHeight: '22px',
  color: colors.black,
  textAlign: 'center',
  margin: '0 0 32px',
});

const Label = styled('label')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  fontSize: 14,
  color: colors.black,
  marginBottom: 24,
});

const Input = styled('input')(({hasError}) => ({
  height: 49,
  border: `1px solid ${hasError ? '#D80B0B' : 'rgba(121, 124, 132, 0.23)'}`,
  borderRadius: 4,
  marginTop: 6,
  paddingLeft: 20,
  paddingRight: 20,
  outline: 0,
  fontSize: 16,
  color: hasError ? '#D80B0B' : colors.black,
  '&:focus': {
    backgroundColor: colors.white,
  },
}));

const ErrorText = styled('div')({
  color: '#D80B0B',
  fontSize: 12,
  marginTop: 6,
  textAlign: 'center',
});

const ConnectLink = styled('span')({
  fontSize: 14,
  lineHeight: '22px',
  color: colors.caramel,
  textDecoration: 'underline',
  cursor: 'pointer',
});

const ForgotPasswordModal = ({
  values,
  errors,
  handleChange,
  handleSubmit,
  mutationLoading,
  isSubmitted,
  onClose,
}) => {
  const isSuccess = isSubmitted;

  return (
    <Overlay onClick={onClose}>
      <Sheet onClick={e => e.stopPropagation()}>
        <ModalForm onSubmit={handleSubmit}>
          <CloseButton type="button" onClick={onClose}>
            &times;
          </CloseButton>
          <Title>Mot de passe oublié</Title>

          {isSuccess ? (
            <>
              <Description>
                Un email contenant le lien de création
                <br />
                du nouveau mot de passe a été envoyé à
                <br />
                {values.email}
              </Description>
              <ConnectLink onClick={onClose}>Se connecter</ConnectLink>
            </>
          ) : (
            <>
              <Description>
                Vous avez oublié votre mot de passe ?
                <br />
                Pas d’inquiétude, nous allons vous assister
                <br />
                pour en créer un nouveau.
              </Description>
              <Label htmlFor="resetEmail">
                Saisissez votre email *
                <Input
                  hasError={!!errors.email}
                  type="email"
                  id="resetEmail"
                  value={values.email}
                  onChange={handleChange('email')}
                />
                {errors.email && <ErrorText>{errors.email}</ErrorText>}
              </Label>
              <Submit disabled={mutationLoading} color="white" style={{width: '100%'}}>
                {mutationLoading ? 'Envoi...' : 'Valider'}
              </Submit>
            </>
          )}
        </ModalForm>
      </Sheet>
    </Overlay>
  );
};

ForgotPasswordModal.propTypes = formProps;

export default ForgotPasswordModal
  |> form({
    validateOnChange: false,
    validateOnBlur: false,
    mapPropsToValues: () => ({email: ''}),
    validate: values => {
      const errors = {};
      if (!values.email) {
        errors.email = 'L’email est requis';
      }
      return errors;
    },
    handleSubmit: ({email}, {props: {postPasswordReset, setSubmitted}}) => {
      postPasswordReset({email, onSuccess: setSubmitted});
    },
  })
  |> withStateHandlers({isSubmitted: false}, {setSubmitted: () => () => ({isSubmitted: true})})
  |> mutate('postPasswordReset');
